/**
 * @module @repo/global-login-sdk/redirect
 * Redirect- und Popup-Flow zur zentralen, gebrandeten Login-App.
 */
import { type GlobalLoginConfig, DEFAULT_LOGIN_URL } from './config';
import { LOGIN_MESSAGE_TYPE } from './protocol';

/**
 * Baut die URL zur zentralen Login-App inkl. App-Branding + Rücksprung-Ziel.
 *
 * @param cfg App-Login-Konfiguration.
 * @param returnTo Optionales Rücksprung-Ziel (default: aktuelle URL bzw. Login-Basis).
 * @param opts Zusätzliche Optionen; `popup: true` hängt `&popup=1` an (Popup-Modus).
 * @returns Die vollständige Login-URL.
 */
export function buildLoginUrl(
  cfg: GlobalLoginConfig,
  returnTo?: string,
  opts?: { popup?: boolean },
): string {
  const base = cfg.loginUrl ?? DEFAULT_LOGIN_URL;
  const redirect = returnTo ?? (typeof window !== 'undefined' ? window.location.href : base);
  const params = new URLSearchParams({ app: cfg.appSlug, redirect });
  if (opts?.popup) params.set('popup', '1');
  return `${base}/login?${params.toString()}`;
}

/**
 * Leitet zum zentralen Login um (gebrandet pro App). No-op außerhalb des Browsers.
 *
 * @param cfg App-Login-Konfiguration.
 * @param returnTo Optionales Rücksprung-Ziel (default: aktuelle URL).
 */
export function redirectToLogin(cfg: GlobalLoginConfig, returnTo?: string): void {
  if (typeof window === 'undefined') return;
  window.location.assign(buildLoginUrl(cfg, returnTo));
}

/** Zusätzliche Optionen für {@link loginPopup}. */
export interface LoginPopupOptions {
  /**
   * Optionales Abbruch-Signal. Wird es ausgelöst (z. B. beim Unmount des
   * aufrufenden Consumers), räumt der Flow Listener/Timer ab, schließt das
   * Popup-Fenster und resolved `false` — so bleibt kein verwaistes Fenster und
   * keine Callback-Referenz auf eine bereits demontierte Komponente zurück.
   */
  signal?: AbortSignal;
}

/**
 * Öffnet den zentralen Login als Popup und wartet auf dessen Ergebnis. Gedacht
 * für Flows, die inline (im eingebetteten Kontext) nicht möglich sind — v. a.
 * Google-OAuth. Akzeptiert AUSSCHLIESSLICH `postMessage`-Antworten von der exakten
 * Login-Origin (`new URL(cfg.loginUrl ?? DEFAULT_LOGIN_URL).origin`) UND aus genau
 * diesem Popup-Fenster (`event.source === win`) mit `event.data.type ===
 * 'gmolike-login'` und resolved `event.data.ok === true`. Fällt das Popup ohne
 * Nachricht zu (z. B. Nutzer schließt es), pollt der Fallback `popup.closed` und
 * resolved `false`. Listener, Intervall und Timeout werden stets sauber abgeräumt
 * und das Fenster über den zentralen `cleanup()` geschlossen (Erfolgs-, Timeout-
 * und Abbruch-Pfad). No-op-sicher außerhalb des Browsers (resolved `false`).
 *
 * @param cfg App-Login-Konfiguration (liefert Origin + Branding-Slug).
 * @param returnTo Optionales Rücksprung-Ziel für den Login.
 * @param opts Zusätzliche Optionen, siehe {@link LoginPopupOptions} (u. a. `signal`).
 * @returns `true`, wenn der Login erfolgreich bestätigt wurde, sonst `false`.
 */
export function loginPopup(
  cfg: GlobalLoginConfig,
  returnTo?: string,
  opts?: LoginPopupOptions,
): Promise<boolean> {
  if (typeof window === 'undefined') return Promise.resolve(false);

  const signal = opts?.signal;
  if (signal?.aborted) return Promise.resolve(false);

  let expectedOrigin: string;
  try {
    expectedOrigin = new URL(cfg.loginUrl ?? DEFAULT_LOGIN_URL).origin;
  } catch {
    return Promise.resolve(false);
  }

  const features =
    'popup=1,width=480,height=720,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes';
  const popup = window.open(
    buildLoginUrl(cfg, returnTo, { popup: true }),
    'gmolike-login',
    features,
  );
  if (!popup) return Promise.resolve(false);
  // Explizit auf `Window` binden, damit die Closures unten die Nicht-Null-Narrowing
  // sicher übernehmen (control-flow-Narrowing greift in hoisted Closures nicht zuverlässig).
  const win: Window = popup;

  return new Promise<boolean>((resolve) => {
    let settled = false;

    function cleanup(): void {
      window.removeEventListener('message', onMessage);
      window.clearInterval(pollId);
      window.clearTimeout(timeoutId);
      signal?.removeEventListener('abort', onAbort);
      // Fenster zentral schließen — deckt Erfolgs-, Timeout- UND Abbruch-Pfad ab,
      // sodass kein verwaistes Login-Fenster stehenbleibt (L1).
      try {
        win.close();
      } catch {
        // Cross-Origin-Popup lässt sich evtl. nicht schließen — bewusst ignorieren.
      }
    }

    function finish(ok: boolean): void {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(ok);
    }

    function onAbort(): void {
      finish(false);
    }

    function onMessage(event: MessageEvent): void {
      if (event.origin !== expectedOrigin) return;
      // Defense-in-depth: nur Nachrichten aus genau diesem Popup akzeptieren,
      // zusätzlich zur Origin-Prüfung (LOW-1).
      if (event.source !== win) return;
      const data = event.data as { type?: unknown; ok?: unknown } | null;
      if (!data || data.type !== LOGIN_MESSAGE_TYPE) return;
      finish(data.ok === true);
    }

    window.addEventListener('message', onMessage);
    const pollId = window.setInterval(() => {
      if (win.closed) finish(false);
    }, 500);
    // Sicherheitsnetz: nach 5 Minuten ohne Antwort abbrechen.
    const timeoutId = window.setTimeout(() => finish(false), 5 * 60 * 1000);
    signal?.addEventListener('abort', onAbort);
  });
}
